import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DATE_FORMATS_LUXON, formatISOFromUTC } from '@/utils/dateHelpers'
import {
  selectCompletedAtForOnboardingStep,
  selectHasUserCompletedOnboardingPhase,
  selectLastStartedAtStepForOnboardingPhase,
} from '@/features/onboarding/user-onboarding-steps/onboarding.selectors'
import {
  OnboardingStepId,
  OnboardingStepPhase,
} from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { fetchUserOnboardingStepsIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.actions'
import { Button, Icon, Text, CardVariants, CardColors, HeroCard } from '@/ui'
import { ImageBackgroundColors, TextColors, TypographyStyles } from '@/styles'

export interface LearnWithHeardItemProps {
  imageBackgroundColor: string
  imageSrc: string
  imageAlt: string
  title: string
  description: string
  navigateTo: string
  timeItTakes: string
  key: string
  onboardingStepPhase: OnboardingStepPhase
  completionStep: OnboardingStepId
}

export const LearnWithHeardItem = ({
  imageBackgroundColor,
  imageSrc,
  imageAlt,
  title,
  description,
  navigateTo,
  timeItTakes,
  onboardingStepPhase,
  completionStep,
}: LearnWithHeardItemProps) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const ahaMomentPhaseComplete = useReselector(
    selectHasUserCompletedOnboardingPhase,
    onboardingStepPhase
  )

  const phaseCompletedAtDate = useReselector(
    selectCompletedAtForOnboardingStep,
    completionStep
  )

  const lastStartedAtStep = useReselector(
    selectLastStartedAtStepForOnboardingPhase,
    onboardingStepPhase
  )

  const navigateStep = useMemo(
    () =>
      // if the user has completed the bookkeeping phase, navigate to the base url to start at the beginning
      !ahaMomentPhaseComplete && lastStartedAtStep
        ? lastStartedAtStep.onboardingStepId.toLowerCase()
        : undefined,
    [ahaMomentPhaseComplete, lastStartedAtStep]
  )

  const navigateToUrl = navigateStep
    ? `${navigateTo}?step=${navigateStep}`
    : navigateTo

  useEffect(() => {
    dispatch(fetchUserOnboardingStepsIfNeeded())
  }, [dispatch])

  return (
    <HeroCard
      color={CardColors.LEVEL_0}
      imageAlt={imageAlt}
      imageColor={imageBackgroundColor as ImageBackgroundColors}
      imageSrc={imageSrc}
      imageWidth={100}
      description={description}
      title={title}
      variant={CardVariants.STACK}
    >
      <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
        {phaseCompletedAtDate &&
          `Reviewed on ${formatISOFromUTC(
            phaseCompletedAtDate,
            DATE_FORMATS_LUXON.DISPLAY_SHORT
          )}`}
      </Text>
      <Button
        variant={ahaMomentPhaseComplete ? 'secondary' : 'primary'}
        onClick={() => navigate(navigateToUrl)}
      >
        {ahaMomentPhaseComplete ? 'Review' : 'Learn More'}
        <Icon icon={regular('arrow-right')} className="ml-2" />
      </Button>
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.MUTED}
        className="flex flex-row items-center"
      >
        <Icon icon={regular('timer')} size="1x" />
        {timeItTakes}
      </Text>
    </HeroCard>
  )
}
